import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { apiErrorMessage } from '../../shared/api/errors';
import { Button } from '../../shared/ui/Button';
import { Card, ErrorText, Spinner } from '../../shared/ui/primitives';
import { buildPanorama, getPanoramaPreview } from './api';

interface Point {
  x: number;
  y: number;
}

/**
 * Ручная подгонка дуги зубного ряда: показывает top-view скана с
 * авто-детектированной дугой (см. getPanoramaPreview), даёт перетащить её
 * опорные точки и пересчитать панораму по подправленной дуге (buildPanorama
 * с curve). Авто-детекция остаётся основным путём — это дополнение поверх неё.
 */
export function PanoramaCurveEditor({
  examinationId,
  onClose,
  onBuilt,
}: {
  examinationId: string;
  onClose: () => void;
  onBuilt: () => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [points, setPoints] = useState<Point[] | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const preview = useQuery({
    queryKey: ['examination', examinationId, 'panorama-preview'],
    queryFn: () => getPanoramaPreview(examinationId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (preview.data && !points) setPoints(preview.data.points);
  }, [preview.data, points]);

  const rebuild = useMutation({
    mutationFn: () =>
      buildPanorama(examinationId, {
        curve: (points ?? []).map((p) => [p.x, p.y] as [number, number]),
      }),
    onSuccess: onBuilt,
  });

  const toImagePoint = (clientX: number, clientY: number): Point | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (dragIndex === null) return;
    const p = toImagePoint(e.clientX, e.clientY);
    if (!p) return;
    setPoints((prev) => (prev ? prev.map((pt, i) => (i === dragIndex ? p : pt)) : prev));
  };

  const stopDrag = () => setDragIndex(null);

  if (preview.isLoading) {
    return (
      <Card className="space-y-3 p-4 sm:p-5">
        <Spinner label="Строим предпросмотр — декодируем объём, это может занять минуту…" />
      </Card>
    );
  }
  if (preview.isError || !preview.data) {
    return (
      <Card className="space-y-3 p-4 sm:p-5">
        <ErrorText>{apiErrorMessage(preview.error, 'Не удалось построить предпросмотр')}</ErrorText>
        <Button type="button" variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
      </Card>
    );
  }

  const { width, height, image } = preview.data;
  const dotR = Math.max(5, width / 120);
  const pathD = points && points.length > 0 ? `M ${points.map((p) => `${p.x},${p.y}`).join(' L ')}` : '';

  return (
    <Card className="space-y-3 p-4 sm:p-5">
      <p className="text-sm font-semibold text-ink">
        Подгонка дуги зубного ряда — перетащите точки по контуру зубов, затем «Пересчитать»
      </p>
      <div className="overflow-hidden rounded-xl border border-line bg-black">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          className="h-auto w-full touch-none select-none"
          onPointerMove={onPointerMove}
          onPointerUp={stopDrag}
          onPointerLeave={stopDrag}
        >
          <image href={image} x={0} y={0} width={width} height={height} />
          {pathD && (
            <path d={pathD} fill="none" stroke="#22c55e" strokeWidth={Math.max(2, width / 300)} />
          )}
          {points?.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={dotR}
              fill={dragIndex === i ? '#f59e0b' : '#22c55e'}
              stroke="#0a0a0a"
              strokeWidth={1}
              className="cursor-grab"
              onPointerDown={(e) => {
                e.preventDefault();
                (e.target as Element).setPointerCapture(e.pointerId);
                setDragIndex(i);
              }}
            />
          ))}
        </svg>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" disabled={rebuild.isPending} onClick={() => rebuild.mutate()}>
          {rebuild.isPending ? 'Пересчитываем…' : 'Пересчитать панораму'}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setPoints(preview.data!.points)}>
          Сбросить к авто-дуге
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
        {rebuild.isError && (
          <span className="text-sm text-danger">
            {apiErrorMessage(rebuild.error, 'Не удалось пересчитать панораму')}
          </span>
        )}
      </div>
    </Card>
  );
}
